/**
 * Site usage → Science site: reading on zitmus.com and zitmus.buzzbrain.ai — its
 * filter bar and its tabs. The BuzzBrain app report has the same tabs in the
 * same order (../app/AppUsageReport.tsx). Its filters narrow the SESSIONS
 * before anything is counted (siteSessionFilters.ts on the backend), so every
 * tab describes the same people.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchFilterBar, TabStrip, type FilterDef } from '@shared-ui/components';
import { siteAnalyticsApi, type SiteAnalytics as Data, type SiteScope } from '../../../api/siteAnalyticsApi';
import { firstTouchLabel, referrerKindLabel } from '../shared/sourceLabels';
import SiteFlowChart from '../shared/SiteFlowChart';
import { ScienceOverview, ScienceTables } from './ScienceTabs';
import { VisitOutcomes } from './VisitOutcomes';
import { PathsView } from './ReadingPaths';
import SiteLandings from './SiteLandings';
import SegmentActivity from './SegmentActivity';
import LinkActivity from './LinkActivity';

/** The science site's own views. */
type Tab = 'overview' | 'tables' | 'funnel' | 'flow' | 'landings' | 'paths';
const TABS = [
  { key: 'overview' as const, label: 'Overview' },
  { key: 'tables' as const, label: 'Tables' },
  { key: 'funnel' as const, label: 'What sessions did' },
  { key: 'flow' as const, label: 'Flow' },
  { key: 'landings' as const, label: 'All landings' },
  { key: 'paths' as const, label: 'Paths' },
];

const DEVICE_LABEL: Record<string, string> = { mobile: 'Phone', tablet: 'Tablet', desktop: 'Desktop' };

/**
 * The page's filter bar: who, which host, and the session filters, each option
 * with its session count. Built from what the backend reports for the window,
 * so a backend without host or session filters (an older deploy) is offered
 * only what it can answer — a filter it would ignore must not be on screen.
 */
function pageFilterDefs(data: Data | null): FilterDef[] {
  const n = (count: number | undefined) => (count === undefined ? '' : ` (${count.toLocaleString()})`);
  const defs: FilterDef[] = [{
    key: 'who', kind: 'select', placeholder: 'Everyone',
    options: [
      { value: 'guest', label: `Guests${n(data?.audienceSplit.guest)}` },
      { value: 'account', label: `Signed in${n(data?.audienceSplit.account)}` },
      { value: 'none', label: `No account${n(data?.audienceSplit.none)}` },
    ],
  }];
  if (data?.hostSplit) {
    const hs = data.hostSplit;
    defs.push({
      key: 'host', kind: 'select', placeholder: 'Both hosts',
      options: [
        { value: 'zitmus.com', label: `zitmus.com${n(hs['zitmus.com'] ?? 0)}` },
        { value: 'zitmus.buzzbrain.ai', label: `zitmus.buzzbrain.ai${n(hs['zitmus.buzzbrain.ai'] ?? 0)}` },
        // Sessions from before the host was kept (25 Sep 2026); offered only while there are some.
        ...(hs.unrecorded ? [{ value: 'unrecorded', label: `Host not recorded${n(hs.unrecorded)}` }] : []),
      ],
    });
  }
  const o = data?.filterOptions;
  if (o) {
    const opts = (list: { value: string; sessions: number }[], label: (v: string) => string = v => v) =>
      list.map(x => ({ value: x.value, label: `${label(x.value)}${n(x.sessions)}` }));
    defs.push(
      { key: 'referrer', kind: 'select', placeholder: 'Any referrer', options: opts(o.referrer, referrerKindLabel) },
      { key: 'landing', kind: 'select', placeholder: 'Any landing page', options: opts(o.landing) },
      { key: 'did', kind: 'select', placeholder: 'Did anything', options: [
        { value: 'simulation', label: 'Opened a simulation' },
        { value: 'lab', label: 'Opened the lab' },
        { value: 'bounce', label: 'Read one page only' },
      ] },
      { key: 'device', kind: 'select', placeholder: 'Any device', options: opts(o.device, d => DEVICE_LABEL[d] ?? d) },
      // Person-level from here: the account's first touch, frozen at its first
      // arrival. A session with no account matches none of these.
      { key: 'source', kind: 'select', placeholder: 'Any first source', options: opts(o.source, firstTouchLabel) },
      { key: 'campaign', kind: 'select', placeholder: 'Any campaign', options: opts(o.campaign) },
      { key: 'returning', kind: 'select', placeholder: 'New & returning', options: [
        { value: 'new', label: 'New — account made in this window' },
        { value: 'returning', label: 'Returning — account made before' },
      ] },
    );
  }
  return defs;
}

/**
 * The science site: who read, and what they read. Its filters narrow the
 * SESSIONS before anything is counted (siteSessionFilters.ts on the backend),
 * so the cards, the chart and every tab below describe the same people.
 */
export function ScienceSiteReport({ from, to, scopeValues, setScopeValues }: {
  from: Date; to: Date;
  scopeValues: Record<string, string>;
  setScopeValues: (v: Record<string, string>) => void;
}) {
  const scope = scopeValues as SiteScope;
  const [tab, setTab] = useState<Tab>('overview');
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (a: Date, b: Date, sc: SiteScope) => {
    setLoading(true); setError(null);
    try {
      setData(await siteAnalyticsApi.get(a, b, sc));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load site usage.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(from, to, scopeValues as SiteScope); }, [from, to, scopeValues, load]);


  const scopeFilters = useMemo(() => pageFilterDefs(data), [data]);

  return (
    <>
      {/* Who the numbers are about, and how they got here. Counts beside each
          option are over the window's sessions before these filters, so the
          reader sees what they are choosing between — a value that is not in
          the window is not offered. */}
      <SearchFilterBar
        filters={scopeFilters}
        values={scopeValues}
        onChange={setScopeValues}
        hideSearch
        hideFetch
      />
      {error && <p className="sa-error">{error}</p>}
      {loading && <p className="sa-empty">Loading…</p>}

      {data && !loading && (
        <>
          <TabStrip tabs={TABS} active={tab} onChange={setTab} ariaLabel="Science site views" className="sa-tabs" />

          {tab === 'overview' && <ScienceOverview data={data} />}
          {tab === 'tables' && <ScienceTables data={data} />}

          {tab === 'funnel' && (
            <>
              <VisitOutcomes data={data} />
              <SegmentActivity from={from} to={to} />
              <LinkActivity from={from} to={to} />
            </>
          )}
          {tab === 'flow' && <SiteFlowChart from={from} to={to} scope={scope} />}
          {tab === 'landings' && <SiteLandings from={from} to={to} scope={scope} />}
          {tab === 'paths' && <PathsView data={data} />}
        </>
      )}
    </>
  );
}
